import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
  type?: 'spinner' | 'skeleton' | 'pulse' | 'dots';
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private loadingSubject = new BehaviorSubject<LoadingState>({
    isLoading: false,
    message: '',
    type: 'spinner'
  });

  private requestCount = 0;
  private loadingRequestsMap = new Map<string, boolean>();

  public loading$: Observable<LoadingState> = this.loadingSubject.asObservable();

  constructor() {}

  /**
   * Show loading with optional message and type
   */
  show(message?: string, type: 'spinner' | 'skeleton' | 'pulse' | 'dots' = 'spinner'): void {
    this.requestCount++;
    this.updateLoadingState(true, message, type);
  }

  /**
   * Hide loading
   */
  hide(): void {
    if (this.requestCount > 0) {
      this.requestCount--;
    }
    
    if (this.requestCount === 0) {
      this.updateLoadingState(false);
    }
  }

  /**
   * Show loading for a specific request identifier
   */
  showForRequest(requestId: string, message?: string, type: 'spinner' | 'skeleton' | 'pulse' | 'dots' = 'spinner'): void {
    this.loadingRequestsMap.set(requestId, true);
    this.updateLoadingState(true, message, type);
  }

  /**
   * Hide loading for a specific request identifier
   */
  hideForRequest(requestId: string): void {
    this.loadingRequestsMap.delete(requestId);
    
    if (this.loadingRequestsMap.size === 0 && this.requestCount === 0) {
      this.updateLoadingState(false);
    }
  }

  /**
   * Force hide all loading states
   */
  hideAll(): void {
    this.requestCount = 0;
    this.loadingRequestsMap.clear();
    this.updateLoadingState(false);
  }

  /**
   * Get current loading state
   */
  isLoading(): boolean {
    return this.loadingSubject.value.isLoading;
  }

  /**
   * Show loading for page navigation
   */
  showPageLoading(message: string = 'Loading page...'): void {
    this.showForRequest('page-navigation', message, 'pulse');
  }

  /**
   * Hide loading for page navigation
   */
  hidePageLoading(): void {
    this.hideForRequest('page-navigation');
  }

  /**
   * Show loading for API calls
   */
  showApiLoading(apiEndpoint?: string): void {
    const requestId = apiEndpoint || 'api-call';
    this.showForRequest(requestId, 'Loading...', 'spinner');
  }

  /**
   * Hide loading for API calls
   */
  hideApiLoading(apiEndpoint?: string): void {
    const requestId = apiEndpoint || 'api-call';
    this.hideForRequest(requestId);
  }

  private updateLoadingState(isLoading: boolean, message?: string, type?: 'spinner' | 'skeleton' | 'pulse' | 'dots'): void {
    const currentState = this.loadingSubject.value;
    this.loadingSubject.next({
      isLoading,
      message: isLoading ? (message || currentState.message || 'Loading...') : '',
      type: isLoading ? (type || currentState.type || 'spinner') : 'spinner'
    });
  }
}